import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, Typography, List, Button, Empty, Modal, message } from 'antd';
import { DeleteOutlined, HistoryOutlined, DeleteFilled, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import HistoryUtils from '../utils/historyUtils';

const { Title, Text } = Typography;

// 动画变体定义
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20
    }
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};

const HistoryRecord = forwardRef(({ onSelectRecord }, ref) => {
  const [historyList, setHistoryList] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    refreshHistory: () => {
      loadHistoryFromStorage();
    }
  }));

  // 组件加载时从本地存储获取历史记录
  useEffect(() => {
    loadHistoryFromStorage();
  }, []);

  // 从本地存储加载历史记录
  const loadHistoryFromStorage = () => {
    setLoading(true);
    try {
      const history = HistoryUtils.getHistory();
      // 确保每条记录都有唯一ID
      const validHistory = history.filter(item => item && item.id);
      setHistoryList(validHistory);
    } catch (error) {
      console.error('加载历史记录失败:', error);
      messageApi.error('加载历史记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 手动刷新历史记录
  const handleManualRefresh = () => {
    messageApi.info('正在刷新历史记录...');
    loadHistoryFromStorage();
  };

  // 删除单条历史记录
  const handleDelete = (id) => {
    if (HistoryUtils.deleteHistory(id)) {
      setHistoryList(prevList => prevList.filter(item => item.id !== id));
      messageApi.success('删除成功');
    } else {
      messageApi.error('删除失败');
    }
  };

  // 确认删除所有历史记录
  const handleClearAll = () => {
    Modal.confirm({
      title: '确认清空',
      content: '确定要清空所有历史记录吗？此操作不可恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        try {
          if (HistoryUtils.clearHistory()) {
            setHistoryList([]); // 立即清空状态
            messageApi.success('已清空所有历史记录');
          } else {
            messageApi.error('清空失败');
          }
        } catch (error) {
          console.error('清空历史记录失败:', error);
          messageApi.error('清空失败');
        }
      }
    });
  };

  // 复用历史记录的参数
  const handleReuse = (record) => {
    if (onSelectRecord && typeof onSelectRecord === 'function') {
      onSelectRecord({
        type: record.type,
        encryptMode: record.encryptMode,
        paddingMode: record.paddingMode,
        keyLength: record.keyLength,
        key: record.key,
        iv: record.iv,
        inputText: record.inputText
      });
      messageApi.success('已加载到表单');
    }
  };

  return (
    <div className="history-section" style={{ marginTop: '24px' }}>
      {contextHolder}
      <Card>
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
        >
          <motion.div 
            style={{ display: 'flex', alignItems: 'center' }}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          >
            <HistoryOutlined style={{ fontSize: '18px', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>历史记录</Title>
          </motion.div>
          <div>
            <motion.div 
              style={{ display: 'inline-block', marginRight: '8px' }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                刷新
              </Button>
            </motion.div>
            {historyList.length > 0 && (
              <motion.div 
                style={{ display: 'inline-block' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  icon={<DeleteFilled />} 
                  danger
                  onClick={handleClearAll}
                >
                  清空记录
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>

        {historyList.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <List
              loading={loading}
              itemLayout="vertical"
              rowKey={record => record.id}
              dataSource={historyList}
              renderItem={(item) => (
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={item.id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                  >
                    <List.Item
                      key={item.id}
                      actions={[
                        <motion.div
                          key="reuse"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Button 
                            icon={<CopyOutlined />} 
                            size="small" 
                            type="primary"
                            onClick={() => handleReuse(item)}
                          >
                            复用
                          </Button>
                        </motion.div>,
                        <motion.div
                          key="delete"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Button 
                            icon={<DeleteOutlined />} 
                            size="small" 
                            danger
                            onClick={() => handleDelete(item.id)}
                          >
                            删除
                          </Button>
                        </motion.div>
                      ]}
                      style={{ 
                        background: '#f9f9f9', 
                        padding: '16px', 
                        borderRadius: '8px',
                        marginBottom: '16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>{item.type}操作</Text>
                        <Text type="secondary" style={{ marginLeft: '16px' }}>{item.time}</Text>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                        <Text type="secondary">加密模式: {item.encryptMode || 'CBC'}</Text>
                        <Text type="secondary">填充模式: {item.paddingMode}</Text>
                        <Text type="secondary">密钥长度: {item.keyLength}位</Text>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px', marginBottom: '8px' }}>
                        <Text type="secondary" style={{ wordBreak: 'break-all' }}>密钥: {item.key}</Text>
                        <Text type="secondary" style={{ wordBreak: 'break-all' }}>初始向量: {item.iv}</Text>
                      </div>
                      <motion.div 
                        style={{ background: '#fff', padding: '8px', borderRadius: '4px' }}
                        whileHover={{ scale: 1.01, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      >
                        <Text
                          ellipsis={{ 
                            tooltip: item.result
                          }}
                          style={{ maxWidth: '100%' }}
                          copyable={{ tooltips: ['复制', '已复制'] }}
                        >
                          {item.result}
                        </Text>
                      </motion.div>
                    </List.Item>
                  </motion.div>
                </AnimatePresence>
              )}
            />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <Empty description={loading ? "加载中..." : "暂无历史记录"} />
          </motion.div>
        )}
      </Card>
    </div>
  );
});

export default HistoryRecord;